import React, { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  LineChart,
  Line,
  AreaChart,
  ComposedChart,
  Cell,
  Legend,
  ResponsiveContainer,
  Area,
} from "recharts";

import c1 from "../../Images/cardIcon/c1.png";
import c2 from "../../Images/cardIcon/c2.png";
import c3 from "../../Images/cardIcon/c3.png";
import c4 from "../../Images/cardIcon/c4.png";

import AdminDealershipList from "./AdminDealershipList ";
import SelectionLayout from "../f&i_Product/SelectionLayout/SelectionLayout";
import { useTheme } from "../BrandColors/Context/ThemeContext";

const AdminDashboard = () => {
  const user = JSON.parse(localStorage.getItem("user"));
  const adminTheme = useTheme() || "#3b82f6";
  const [selectedOption, setSelectedOption] = useState("Month");

  // Card data for different timeframes
  const cardData = {
    Month: [
      { id: 1, img: c1, value: "1k", description: "Pre-Sales Engagement" },
      { id: 2, img: c2, value: "300", description: "Enrollment & Finance" },
      { id: 3, img: c3, value: "2k", description: "F&I Services" },
      { id: 4, img: c4, value: "2k", description: "Total Dealership" },
    ],
    Year: [
      { id: 1, img: c1, value: "12k", description: "Pre-Sales Engagement" },
      { id: 2, img: c2, value: "3.6k", description: "Enrollment & Finance" },
      { id: 3, img: c3, value: "24k", description: "F&I Services" },
      { id: 4, img: c4, value: "24k", description: "Total Dealership" },
    ],
    Week: [
      { id: 1, img: c1, value: "200", description: "Pre-Sales Engagement" },
      { id: 2, img: c2, value: "50", description: "Enrollment & Finance" },
      { id: 3, img: c3, value: "400", description: "F&I Services" },
      { id: 4, img: c4, value: "400", description: "Total Dealership" },
    ],
  };

  const handleSelectChange = (e) => {
    setSelectedOption(e.target.value);
  };

  const Visitor = [
    { month: "Jan", VisitorPart1: 500, VisitorPart2: 600 },
    { month: "Feb", VisitorPart1: 500, VisitorPart2: 600 },
    { month: "Mar", VisitorPart1: 500, VisitorPart2: 600 },
    { month: "Apr", VisitorPart1: 500, VisitorPart2: 250 },
    { month: "May", VisitorPart1: 500, VisitorPart2: 300 },
  ];

  const data = [
    { name: "Mon", revenueA: 20, revenueB: 10, trend: 15 },
    { name: "Tue", revenueA: 30, revenueB: 20, trend: 25 },
    { name: "Wed", revenueA: 25, revenueB: 15, trend: 20 },
    { name: "Thu", revenueA: 50, revenueB: 30, trend: 40 },
    { name: "Fri", revenueA: 20, revenueB: 10, trend: 18 },
    { name: "Sat", revenueA: 30, revenueB: 15, trend: 22 },
    { name: "Sun", revenueA: 40, revenueB: 20, trend: 30 },
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg shadow-xl text-white">
          <p className="font-semibold">{payload[0].value} users</p>
          <p className="text-slate-600 text-sm">Day {label}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="w-full flex flex-col gap-6 animate-fade-in-up">
      
      <header className="mb-2">
        <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
          Welcome, <span style={{ color: adminTheme }}>{user?.email?.split("@")[0].charAt(0).toUpperCase() + user?.email?.split("@")[0].slice(1)}</span>
        </h1>
        <p className="text-slate-600 mt-1">Admin {`(${user?.role})`}</p>
      </header>

      {/* Cards Section */}
      <div className="w-full">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold text-white">Today’s Sales</h3>
          <select
            className="input-field max-w-[120px] py-1.5 px-3 text-sm cursor-pointer"
            value={selectedOption}
            onChange={handleSelectChange}
          >
            <option value="Week" className="bg-blue-50">Week</option>
            <option value="Month" className="bg-blue-50">Month</option>
            <option value="Year" className="bg-blue-50">Year</option>
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {cardData[selectedOption].map((card) => (
            <div key={card.id} className="glass-card p-6 flex items-center gap-4 group">
              <div className="w-16 h-16 rounded-2xl flex-shrink-0 flex items-center justify-center bg-blue-100/50 p-3 shadow-lg group-hover:scale-110 transition-transform">
                <img src={card.img} alt={card.description} className="w-full h-full object-contain filter drop-shadow-md" />
              </div>
              <div className="flex flex-col">
                <h3 className="text-2xl font-bold text-white mb-1">{card.value}</h3>
                <p className="text-sm text-slate-600 leading-tight font-medium">{card.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
        
        {/* Visitor Insights Chart */}
        <div className="glass-panel p-6">
          <div className="flex justify-between items-center mb-6">
            <h5 className="text-lg font-bold text-white">Visitor Insights</h5>
            <select className="input-field max-w-[100px] py-1 px-2 text-xs bg-white border-none">
              <option className="bg-blue-50">Month</option>
              <option className="bg-blue-50">Year</option>
              <option className="bg-blue-50">Week</option>
            </select>
          </div>
          
          <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={Visitor} margin={{ top: 10, right: 10, left: -20, bottom: 0 }} barSize={20}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                <XAxis dataKey="month" stroke="#94a3b8" tick={{ fill: '#94a3b8', fontSize: 12 }} axisLine={false} tickLine={false} dy={10} />
                <YAxis stroke="#94a3b8" tick={{ fill: '#94a3b8', fontSize: 12 }} axisLine={false} tickLine={false} dx={-10} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.05)' }} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: '12px', color: '#94a3b8', paddingTop: '20px' }} />
                <Bar dataKey="VisitorPart1" stackId="a" fill={adminTheme} radius={[0, 0, 4, 4]} />
                <Bar dataKey="VisitorPart2" stackId="a" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Total Revenue Chart */}
        <div className="glass-panel p-6">
          <div className="flex justify-between items-center mb-6">
            <h5 className="text-lg font-bold text-white">Total Revenue</h5>
            <select className="input-field max-w-[100px] py-1 px-2 text-xs bg-white border-none">
              <option className="bg-blue-50">Week</option>
              <option className="bg-blue-50">Month</option>
              <option className="bg-blue-50">Year</option>
            </select>
          </div>
          
          <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }} barSize={30}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                <XAxis dataKey="name" stroke="#94a3b8" tick={{ fill: '#94a3b8', fontSize: 12 }} axisLine={false} tickLine={false} dy={10} />
                <YAxis stroke="#94a3b8" tick={{ fill: '#94a3b8', fontSize: 12 }} axisLine={false} tickLine={false} dx={-10} />
                <Tooltip cursor={{ fill: 'rgba(255,255,255,0.05)' }} contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px', color: '#f8fafc' }} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: '12px', color: '#94a3b8', paddingTop: '20px' }} />

                <Bar dataKey="revenueA" stackId="a" fill={adminTheme} radius={[0, 0, 4, 4]}>
                  {data.map((entry) => (
                    <Cell key={`cell-${entry.name}`} fill={entry.name === "Thu" || entry.name === "Sun" ? adminTheme : "#3b82f6"} />
                  ))}
                </Bar>

                <Bar dataKey="revenueB" stackId="a" fill="#8b5cf6" radius={[4, 4, 0, 0]}> 
                  {data.map((entry) => (
                    <Cell key={`cell-${entry.name}`} fill={entry.name === "Thu" || entry.name === "Sun" ? "#6366f1" : "#a855f7"} />
                  ))}
                </Bar>

                <Line type="monotone" dataKey="trend" stroke="#10b981" strokeWidth={3} dot={{ r: 4, fill: '#0f172a', strokeWidth: 2 }} activeDot={{ r: 6, fill: '#10b981' }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

      </div>

      <div className="w-full mt-4">
        <AdminDealershipList />
      </div>

    </div>
  );
};

export default AdminDashboard;
